import type {
  CategoryParams,
  CursorPaginatedResponse,
  ProductCategory,
  ServiceCategory,
} from '@/libs/interfaces'

import { mockProductCategories, mockServiceCategories } from '../fixtures/categories'
import { MOCK_ORG_ID } from '../fixtures/seed'
import { delay, genID, matchesSearch, nowISO, paginate } from '../helpers'
import { createEntityStore } from '../store'

const serviceCategoryStore = createEntityStore<ServiceCategory, 'serviceCategoryID'>(
  mockServiceCategories,
  'serviceCategoryID',
)
const productCategoryStore = createEntityStore<ProductCategory, 'productCategoryID'>(
  mockProductCategories,
  'productCategoryID',
)

const filterServiceCategories = (params: CategoryParams): ServiceCategory[] =>
  serviceCategoryStore
    .getAll()
    .filter((category) => matchesSearch(category, params.search, ['serviceCategoryName']))

const filterProductCategories = (params: CategoryParams): ProductCategory[] =>
  productCategoryStore
    .getAll()
    .filter((category) => matchesSearch(category, params.search, ['productCategoryName']))

export const categoryApiMock = {
  createService: async (body: Partial<ServiceCategory>): Promise<Partial<ServiceCategory>> => {
    const category: ServiceCategory = {
      serviceCategoryID: genID('service-category'),
      orgID: MOCK_ORG_ID,
      serviceCategoryName: '',
      serviceCategoryDescription: '',
      createdAt: nowISO(),
      updatedAt: nowISO(),
      ...body,
    }
    serviceCategoryStore.insert(category)
    return delay(category)
  },
  createProduct: async (body: Partial<ProductCategory>): Promise<Partial<ProductCategory>> => {
    const category: ProductCategory = {
      productCategoryID: genID('product-category'),
      orgID: MOCK_ORG_ID,
      productCategoryName: '',
      productCategoryDescription: '',
      createdAt: nowISO(),
      updatedAt: nowISO(),
      ...body,
    }
    productCategoryStore.insert(category)
    return delay(category)
  },
  getByIDService: async (params: Partial<ServiceCategory>): Promise<Partial<ServiceCategory>> =>
    delay(serviceCategoryStore.getById(params.serviceCategoryID ?? '') ?? {}),
  getByIDProduct: async (params: Partial<ProductCategory>): Promise<Partial<ProductCategory>> =>
    delay(productCategoryStore.getById(params.productCategoryID ?? '') ?? {}),
  list: async (params: CategoryParams): Promise<ServiceCategory[]> =>
    delay(filterServiceCategories(params)),
  listCursorProduct: async (
    params: CategoryParams,
  ): Promise<CursorPaginatedResponse<ProductCategory>> =>
    delay(paginate(filterProductCategories(params), params)),
  listCursorService: async (
    params: CategoryParams,
  ): Promise<CursorPaginatedResponse<ServiceCategory>> =>
    delay(paginate(filterServiceCategories(params), params)),
  updateService: async (payload: Partial<ServiceCategory>) => {
    const updated = payload.serviceCategoryID
      ? serviceCategoryStore.update(payload.serviceCategoryID, { ...payload, updatedAt: nowISO() })
      : undefined
    return delay(updated ?? payload)
  },
  updateProduct: async (payload: Partial<ProductCategory>) => {
    const updated = payload.productCategoryID
      ? productCategoryStore.update(payload.productCategoryID, { ...payload, updatedAt: nowISO() })
      : undefined
    return delay(updated ?? payload)
  },
  deleteService: async (payload: Partial<ServiceCategory>) => {
    if (payload.serviceCategoryID) serviceCategoryStore.remove(payload.serviceCategoryID)
    return delay(undefined)
  },
  deleteProduct: async (payload: Partial<ProductCategory>) => {
    if (payload.productCategoryID) productCategoryStore.remove(payload.productCategoryID)
    return delay(undefined)
  },
}
